/**
 * The sun path chart, as an SVG string.
 *
 * ONE GENERATOR, TWO DESTINATIONS.
 *
 * The screen renders this string and the export downloads it, so what is saved
 * is byte-for-byte what was displayed. The first version of this chart drew
 * itself with React elements and exported nothing, which meant a rendering
 * defect could exist on screen with no artefact anyone could inspect -- and one
 * did. A generator that returns a string is a defect you can open in a viewer.
 *
 * The axis is the conventional one: azimuth measured either side of the
 * direction the array faces (south in the northern hemisphere, north in the
 * southern), elevation from the horizon, seven curves for the seven distinct
 * solar declinations rather than twelve monthly tracks five of which lie on top
 * of five others.
 */

const PALETTE = {
  light: {
    surface: "#ffffff",
    ink: "#22303a",
    muted: "#68757f",
    grid: "#dfe3e0",
    sun: "#c2751a",
    land: "#4a6b86",
  },
  dark: {
    surface: "#141a1f",
    ink: "#e3e8e6",
    muted: "#93a0a8",
    grid: "#2c3238",
    sun: "#e8a33c",
    land: "#7fa3c4",
  },
};

const esc = (text) =>
  String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const MONTHS_EL = { Jan: "Ιαν", Feb: "Φεβ", Mar: "Μαρ", Apr: "Απρ", May: "Μάι", Jun: "Ιουν", Jul: "Ιουλ", Aug: "Αυγ", Sep: "Σεπ", Oct: "Οκτ", Nov: "Νοε", Dec: "Δεκ" };
export const sunPathMonthLabel = (value, greek) => greek
  ? String(value).replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g, (month) => MONTHS_EL[month])
  : value;

// How far along its own curve each badge slides, by position in the list.
// Adjacent declinations peak at nearly the same height near the solstices --
// 22 Jun and 22 May differ by three degrees -- so badges placed strictly at the
// apex touch in pairs at the top and bottom of the fan. Sliding them along the
// curve keeps every badge ON the track it names while separating neighbours,
// which nudging them off the curve would not. The same three offsets are in
// pv_core/sun_path_svg.py, so the report and the browser draw one picture.
const BADGE_OFFSETS_DEG = [0, -26, 22];

const badgePoint = (points, position) => {
  const apex = points.reduce((a, b) => (a[1] >= b[1] ? a : b));
  const offset = BADGE_OFFSETS_DEG[position % BADGE_OFFSETS_DEG.length];
  if (!offset) return apex;
  const target = apex[0] + offset;
  return points.reduce((a, b) =>
    Math.abs(a[0] - target) <= Math.abs(b[0] - target) ? a : b);
};

export function buildSunPathSvg(data, options = {}) {
  const paired = data?.paired;
  if (!paired || !paired.trajectories?.length) return null;

  const {
    width = 760,
    height = 440,
    mode = "light",
    title = null,
    subtitle = null,
    greek = false,
  } = options;
  const c = PALETTE[mode] || PALETTE.light;

  const legendW = 168;
  const pad = { top: title ? 34 : 16, right: legendW + 22, bottom: 40, left: 44 };

  const [azMin, azMax] = paired.azimuth_range || [-120, 120];
  const elMax = Math.max(
    10,
    Math.ceil(
      Math.max(...paired.trajectories.flatMap((t) => t.points.map((p) => p[1]))) / 15
    ) * 15
  );

  const X = (az) =>
    pad.left + ((az - azMin) / (azMax - azMin)) * (width - pad.left - pad.right);
  const Y = (el) =>
    height - pad.bottom - (el / elMax) * (height - pad.top - pad.bottom);

  // Points outside the drawn azimuth window are dropped rather than clamped: a
  // clamped point pins the curve to the frame edge and draws a vertical wall
  // that is not a sun path.
  const clip = (points) => points.filter(([az]) => az >= azMin && az <= azMax);

  const poly = (points) =>
    points
      .map(([az, el], i) => `${i ? "L" : "M"}${X(az).toFixed(1)},${Y(el).toFixed(1)}`)
      .join("");

  const label = (x, y, text, { fill, size = 9, weight = 400, anchor = "middle" } = {}) => {
    const common = `x="${x.toFixed(1)}" y="${y.toFixed(1)}" text-anchor="${anchor}" font-size="${size}" font-weight="${weight}" font-family="system-ui, -apple-system, Segoe UI, sans-serif"`;
    return (
      `<text ${common} fill="none" stroke="${c.surface}" stroke-width="3" stroke-linejoin="round">${esc(text)}</text>` +
      `<text ${common} fill="${fill}">${esc(text)}</text>`
    );
  };

  const out = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="${width}" height="${height}" fill="${c.surface}"/>`,
  ];

  if (title) {
    out.push(
      `<text x="${pad.left}" y="18" font-size="13" font-weight="700" fill="${c.ink}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${esc(title)}</text>`
    );
    if (subtitle) {
      out.push(
        `<text x="${pad.left}" y="30" font-size="9.5" fill="${c.muted}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${esc(subtitle)}</text>`
      );
    }
  }

  // ---- grid -------------------------------------------------------------
  for (let el = 0; el <= elMax; el += 15) {
    out.push(
      `<line x1="${pad.left}" x2="${(width - pad.right).toFixed(1)}" y1="${Y(el).toFixed(1)}" y2="${Y(el).toFixed(1)}" stroke="${c.grid}" stroke-width="1"/>`
    );
    out.push(label(pad.left - 7, Y(el) + 3, `${el}`, { fill: c.muted, size: 8.5, anchor: "end" }));
  }
  for (let az = azMin; az <= azMax; az += 30) {
    out.push(
      `<line x1="${X(az).toFixed(1)}" x2="${X(az).toFixed(1)}" y1="${pad.top}" y2="${(height - pad.bottom).toFixed(1)}" stroke="${c.grid}" stroke-width="1"/>`
    );
    out.push(label(X(az), height - pad.bottom + 14, `${az}`, { fill: c.muted, size: 8.5 }));
  }
  const facing = paired.reference === "north" ? "N" : "S";
  out.push(
    label(
      (pad.left + (width - pad.right)) / 2,
      height - pad.bottom + 28,
      greek ? `Αζιμούθιο από ${paired.reference === "north" ? "Βορρά" : "Νότο"} (°) — ανατολικά αρνητικό, δυτικά θετικό` : `Azimuth from ${facing} (°)  —  east negative, west positive`,
      { fill: c.muted, size: 9 }
    )
  );
  out.push(
    `<text transform="translate(12,${((height - pad.bottom + pad.top) / 2).toFixed(1)}) rotate(-90)" text-anchor="middle" font-size="9" fill="${c.muted}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${greek ? "Ύψος ήλιου (°)" : "Sun elevation (°)"}</text>`
  );

  // ---- hour lines, the most recessive layer -----------------------------
  (paired.hour_lines || []).forEach((line) => {
    const pts = clip(line.points);
    if (pts.length < 2) return;
    out.push(
      `<path d="${poly(pts)}" fill="none" stroke="${c.ink}" stroke-opacity="0.22" stroke-width="1"/>`
    );
    // Labelled at the TOP of the arc, not the bottom. The arcs run from the June
    // solstice down to December, so their low ends all converge near the horizon
    // where the numbered badges and the terrain already are; their high ends fan
    // out along the June curve with room between them. Noon is skipped -- the
    // centre line needs no label, and it lands squarely on badge 1.
    const top = pts[0];
    // ...and only where the label has room inside the frame. An arc whose top
    // lies near the edge of the drawn window gets its label clipped in half,
    // which reads as a rendering fault rather than a missing label.
    const roomy = Math.abs(top[0]) < (azMax - 12);
    if (line.hour % 2 === 0 && line.hour !== 12 && roomy) {
      out.push(label(X(top[0]), Y(top[1]) - 6, line.label, { fill: c.muted, size: 8 }));
    }
  });

  // ---- the seven declination curves -------------------------------------
  (paired.trajectories || []).forEach((track, position) => {
    const pts = clip(track.points);
    if (pts.length < 2) return;
    out.push(
      `<path d="${poly(pts)}" fill="none" stroke="${c.sun}" stroke-width="1.6" stroke-linecap="round"/>`
    );
    const apex = badgePoint(pts, position);
    out.push(
      `<circle cx="${X(apex[0]).toFixed(1)}" cy="${Y(apex[1]).toFixed(1)}" r="6.5" fill="${c.surface}" stroke="${c.sun}" stroke-width="1.2"/>`
    );
    out.push(label(X(apex[0]), Y(apex[1]) + 3, String(track.number), { fill: c.sun, size: 8.5, weight: 700 }));
  });

  // ---- terrain, drawn last so it sits in front of what it blocks --------
  const skyline = clip(paired.horizon || []);
  if (skyline.length > 1) {
    const closed = `${poly(skyline)}L${X(skyline[skyline.length - 1][0]).toFixed(1)},${Y(0).toFixed(1)}L${X(skyline[0][0]).toFixed(1)},${Y(0).toFixed(1)}Z`;
    out.push(`<path d="${closed}" fill="${c.land}" fill-opacity="0.38"/>`);
    out.push(`<path d="${poly(skyline)}" fill="none" stroke="${c.land}" stroke-width="1.4"/>`);
  }

  out.push(
    `<line x1="${pad.left}" x2="${(width - pad.right).toFixed(1)}" y1="${Y(0).toFixed(1)}" y2="${Y(0).toFixed(1)}" stroke="${c.muted}" stroke-width="1"/>`
  );

  // ---- legend -----------------------------------------------------------
  const legendX = width - legendW - 8;
  let legendY = pad.top + 6;
  out.push(
    `<text x="${legendX}" y="${legendY}" font-size="9" font-weight="700" fill="${c.ink}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${greek ? "Τροχιές ήλιου" : "Sun tracks"}</text>`
  );
  legendY += 14;
  (paired.legend || []).forEach((row) => {
    out.push(
      `<circle cx="${legendX + 6}" cy="${legendY - 3}" r="6" fill="${c.surface}" stroke="${c.sun}" stroke-width="1.2"/>`
    );
    out.push(
      `<text x="${legendX + 6}" y="${legendY}" text-anchor="middle" font-size="8" font-weight="700" fill="${c.sun}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${row.number}</text>`
    );
    out.push(
      `<text x="${legendX + 18}" y="${legendY}" font-size="9" fill="${c.ink}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${esc(sunPathMonthLabel(row.label, greek))}</text>`
    );
    legendY += 15;
  });

  if (skyline.length > 1) {
    legendY += 6;
    out.push(
      `<rect x="${legendX}" y="${legendY - 8}" width="12" height="8" fill="${c.land}" fill-opacity="0.38" stroke="${c.land}" stroke-width="1"/>`
    );
    out.push(
      `<text x="${legendX + 18}" y="${legendY}" font-size="9" fill="${c.ink}" font-family="system-ui, -apple-system, Segoe UI, sans-serif">${greek ? "Ανάγλυφο ορίζοντα" : "Terrain horizon"}</text>`
    );
  }

  out.push("</svg>");
  return out.join("");
}

export default buildSunPathSvg;
